import {
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { getAssemblyConstituencyById } from "../api/requests/assemblyConstituency";
import { updateUser as UpdateUserProfile } from "../api/requests/users";
import { getZoneById } from "../api/requests/zones";
import { useAlert } from "../context/AlertContext";
import { useAuth } from "../context/AuthContext";
import { AssemblyConstituencyType, ZoneType } from "../utils/types";

const MyProfile: React.FC = () => {
  const { showAlert } = useAlert();
  const { user, updateUser } = useAuth();
  const [editableName, setEditableName] = useState("");
  const [loading, setLoading] = useState<boolean>(false);
  const [zone, setZone] = useState<ZoneType>();
  const [assemblyConstituency, setAssemblyConstituency] =
    useState<AssemblyConstituencyType>();

  useEffect(() => {
    const fetchData = async () => {
      if (!user || !user.userId) return;
      setEditableName(user.fullName);
    };
    fetchData();
  }, [user?.userId]);

  useEffect(() => {
    fetchAssemblyConstituencyData();
    fetchZoneData();
  }, [user?.assemblyConstituencyId, user?.zoneId]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditableName(e.target.value);
  };

  const handleSave = async () => {
    if (!user) return;

    try {
      const updatedUser = {
        userId: user.userId,
        fullName: editableName,
        mobileNo: user.mobileNo,
        rankId: user.rankId,
        policeStation: user.policeStation,
        zoneId: user.zoneId,
        location: user.location,
        roleId: user.roleId,
        assemblyConstituencyId: user.assemblyConstituencyId,
      };
      const response = await UpdateUserProfile(updatedUser);
      showAlert("Profile updated successfully!", "success");

      updateUser({ fullName: editableName });
      if (response && response.status === 200) {
        showAlert("Profile updated successfully!", "success");
      }
    } catch (error) {
      console.error("Error during updateUser API call:", error);
      showAlert("Profile Update failed! Please try again.", "error");
    }
  };

  const fetchAssemblyConstituencyData = async () => {
    setLoading(true);
    if (user?.assemblyConstituencyId) {
      const assemblyConstituencyRes = await getAssemblyConstituencyById(
        user?.assemblyConstituencyId
      );
      if (assemblyConstituencyRes.status == 200) {
        setAssemblyConstituency(assemblyConstituencyRes.data);
      }
    }
    setLoading(false);
  };

  const fetchZoneData = async () => {
    setLoading(true);
    if (user?.zoneId) {
      const zoneRes = await getZoneById(user?.zoneId);
      if (zoneRes.status == 200) {
        setZone(zoneRes.data);
      }
    }
    setLoading(false);
  };

  if (!user) return <div>Loading...</div>;

  return (
    <Paper
      elevation={3}
      sx={{ padding: 4, maxWidth: 600, margin: "auto", marginTop: 4 }}
    >
      <Typography variant="h5" sx={{ mb: 1 }}>
        My Profile
      </Typography>
      <Box display="flex" flexDirection="column" gap={2}>
        <TextField
          label="Full Name"
          value={editableName}
          onChange={handleNameChange}
          fullWidth
        />
        <TextField
          label="Email"
          value={user.email}
          fullWidth
          InputProps={{ readOnly: true }}
          disabled
        />
        <TextField
          label="Mobile Number"
          value={user.mobileNo}
          fullWidth
          InputProps={{ readOnly: true }}
          disabled
        />
        <TextField
          label="Role"
          value={user.roleName}
          fullWidth
          InputProps={{ readOnly: true }}
          disabled
        />
        <TextField
          label="Assesmbly Constituency"
          value={assemblyConstituency?.assemblyConstituencyName || ""}
          fullWidth
          InputProps={{ readOnly: true }}
          disabled
        />
        <TextField
          label="Zone"
          value={zone?.zoneName}
          fullWidth
          InputProps={{ readOnly: true }}
          disabled
        />
        <TextField
          label="Location"
          value={user.location}
          fullWidth
          InputProps={{ readOnly: true }}
          disabled
        />
        {user &&
          user.permissions.some(
            (p) => p.module === "Profile" && p.actions.includes("Edit")
          ) && (
            <Box textAlign="right">
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                sx={{ mt: 2 }}
              >
                Save
              </Button>
            </Box>
          )}
      </Box>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : null}
    </Paper>
  );
};

export default MyProfile;
